import { useCallback, useState, useRef, useEffect } from "react";
import {
  useDeptEmployees,
  usePageSubDeptEmployeesQuery,
} from "@/hooks/services";
import type { PersonnelItem, OperationEvent } from "../type";

// ==================== 常量配置 ====================
const SEARCH_DEBOUNCE_DELAY = 300; // 300ms 防抖延迟

export function usePersonnelSelectorModal() {
  const [eventType, setEventType] = useState<OperationEvent>("search");
  const [selectedPersonnel, setSelectedPersonnel] =
    useState<PersonnelItem | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [selectedDeptNo, setSelectedDeptNo] = useState<string>("");
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 防抖搜索值更新
  useEffect(() => {
    // 清除之前的定时器
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // 设置新的定时器
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, SEARCH_DEBOUNCE_DELAY);

    // 清理函数
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchValue]);

  // 搜索模式使用 useDeptEmployees（接口 /purchase/user/page）
  const searchQueryResult = useDeptEmployees({
    deptNo: "",
    searchValue: debouncedSearchValue,
    pageNum: 1,
    pageSize: 100,
  });

  // 部门选择模式使用 usePageSubDeptEmployeesQuery（接口 /purchase/user/pageSubDeptList）
  const deptQueryResult = usePageSubDeptEmployeesQuery({
    deptNo: selectedDeptNo,
    searchValue: "",
    pageNum: 1,
    pageSize: 100,
  });

  // 根据事件类型选择对应的查询结果
  const employeeList =
    eventType === "search"
      ? searchQueryResult.employeeList
      : deptQueryResult.employeeList;
  const isLoading =
    eventType === "search"
      ? searchQueryResult.isLoading
      : deptQueryResult.isLoading;

  const searchedPersonnelList: PersonnelItem[] =
    employeeList.map((item: any) => ({
      userId: item.id,
      userName: item.userName,
      nickName: item.nickName,
      avatar: item.avatar,
      deptNo: item.deptNo,
    })) || [];

  const reset = useCallback(() => {
    setSelectedPersonnel(null);
    setSearchValue("");
    setDebouncedSearchValue("");
    setSelectedDeptNo("");
    setEventType("search");
  }, []);

  const handleSearch = (value: string) => {
    setSearchValue(value);
    setSelectedPersonnel(null);
    setEventType("search");
  };

  return {
    selectedPersonnel,
    searchValue,
    searchedPersonnelList,
    reset,
    setSelectedPersonnel,
    setSearchValue,
    handleSearch,
    eventType,
    setEventType,
    selectedDeptNo,
    setSelectedDeptNo,
    isLoading,
  };
}
